import type { HttpClient } from '@wix/http-client'
import type { PageRef } from '@wix/editor-platform-sdk-types'
import type { EditorSDK } from '@wix/wix-data-client-common-standalone'
import {
  isEditor3OrStudio2,
  type AppApi,
  type EDITOR_TYPE,
} from '@wix/wix-data-client-common'
import type { DynamicPageUrlCounter } from './types'
import { createDynamicPageUrlCounter } from './dynamicPageUrlCounter'

export const setUpDynamicPageUrlCounter = ({
  editorSdkProxy,
  httpClient,
  finalAppApiPromise,
  editorType,
}: {
  editorSdkProxy: EditorSDK
  httpClient: HttpClient
  finalAppApiPromise: Promise<AppApi>
  editorType: EDITOR_TYPE
}): DynamicPageUrlCounter => {
  const counter = createDynamicPageUrlCounter({
    editorSdkProxy,
    httpClient,
    getGridAppId: async () => {
      const appApi = await finalAppApiPromise
      return appApi.getGridAppId()
    },
  })

  const resetCounter = (appApi: AppApi, pageRefs?: PageRef[]) => {
    if (pageRefs) {
      const eager = isEditor3OrStudio2(editorType)
      counter.reset(pageRefs, eager)
    } else {
      counter.resetAll()
    }

    appApi.emit('dynamicPageUrlCountsChanged')
  }

  finalAppApiPromise.then(appApi => {
    appApi.addListener('contentManagerClosed', () => {
      resetCounter(appApi)
    })
    appApi.addListener('routerDatasetFilterChanged', (pageRef: PageRef) => {
      resetCounter(appApi, [pageRef])
    })
    appApi.addListener('dynamicPageUrlChanged', (pageRef: PageRef) => {
      resetCounter(appApi, [pageRef])
    })
    appApi.addListener('itemPageLinksGenerated', (pageRefs: PageRef[]) => {
      resetCounter(appApi, pageRefs)
    })
    appApi.addListener('slugValuesGenerated', (pageRefs: PageRef[]) => {
      resetCounter(appApi, pageRefs)
    })
    appApi.addListener('routersChanged', ({ pageRefs }) => {
      resetCounter(appApi, pageRefs)
    })
  })

  return {
    getCount: counter.getCount,
    getAllCounts: counter.getAllCounts,
    refreshCounts: async (pageRefs: PageRef[]) => {
      const appApi = await finalAppApiPromise
      resetCounter(appApi, pageRefs)
    },
  }
}
